<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useDropZone } from "@vueuse/core";
import { ButtonSeverity, isNullish, ProfilePictureSize, TuButton, TuInformationIcon, TuInputCopy, TuInputLabel } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation";
import UserAvatar from "@Modules/urls/Components/UserAvatar.vue";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  userName?: string;
  profileSrc?: string;
  loading?: boolean;
  errors?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errors: () => ({}),
});

//
// Emit
interface Events {
  (e: "submit", photo: File): void;
}

const emit = defineEmits<Events>();

//
// Data
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

const dropZoneRef = ref<HTMLElement>();
const fileInputRef = ref<HTMLInputElement>();
const localProfileSrc = ref<string | null>(null);
const localError = ref<string | null | undefined>(null);

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

useDropZone(dropZoneRef, {
  onDrop,
  dataTypes: ["image/jpeg", "image/png"],
  multiple: false,
});

//
// Computed
const avatarPhotoUrl = computed(() => localProfileSrc.value ?? props.profileSrc ?? null);

//
// Methods
function processFile(file: File): void {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    localError.value = __("update_icon_form.file_too_large_error");
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    const result = e.target?.result;

    if (typeof result !== "string") {
      return;
    }

    localError.value = null;
    localProfileSrc.value = result;
    emit("submit", file);
  };
  reader.readAsDataURL(file);
}

function onDrop(files: File[] | null): void {
  const file = files?.[0];

  if (isNullish(file)) {
    return;
  }

  processFile(file);
}

function onFileInputChange(event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0];

  if (isNullish(file)) {
    return;
  }

  processFile(file);
}

function openFileBrowser(): void {
  fileInputRef.value?.click();
}

//
// Watchers
watch(() => props.errors, (givenErrors) => {
  localError.value = givenErrors.photo;
});

//
// Exposed Test Vars
defineExpose({ localError });
</script>

<template>
  <form
    class="icon-profile-form"
    :class="{ 'icon-profile-form--mobile': isMobileView }"
    @submit.prevent
  >
    <UserAvatar
      :user="{
        name: userName,
        photo_url: avatarPhotoUrl,
      }"
      :size="ProfilePictureSize.LARGE"
    />
    <div class="icon-profile-form__upload-input">
      <div class="icon-profile-form__upload-label">
        <TuInputLabel
          id="profile"
          :label="__('update_icon_form.profile_image_label')"
        />
        <TuInformationIcon
          placement="top"
          :tooltip="__('update_icon_form.profile_image_tooltip')"
        />
      </div>
      <TuInputCopy
        class="_error"
        v-bind="{ ...(localError && { errorMessage: localError }) }"
      />
      <div
        ref="dropZoneRef"
        class="icon-profile-form__upload-drop-zone"
        @click="openFileBrowser"
      >
        <p
          v-if="!isMobileView"
          class="icon-profile-form__upload-drop-label"
        >
          <i class="icon-profile-form__upload-drop-icon fa-solid fa-download" />
          {{ __('update_icon_form.drop_zone_label') }}
        </p>
        <TuButton
          class="_browse"
          :label="__('update_icon_form.submit')"
          :severity="ButtonSeverity.SECONDARY"
          :is-disabled="loading"
          :is-block="isMobileView"
          @click.stop="openFileBrowser"
        />
      </div>
      <input
        ref="fileInputRef"
        type="file"
        accept="image/jpeg,image/png"
        class="icon-profile-form__file-input _file_input"
        @change="onFileInputChange"
      >
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.icon-profile-form {
  $self: &;

  display: flex;
  gap: spacing.$xxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__upload-input {
    display: flex;
    flex-flow: column;
  }

  &__upload-label {
    display: flex;
    gap: spacing.$xs-5;
  }

  &__upload-drop-label {
    margin: 0;
  }

  &__upload-drop-icon {
    margin-right: spacing.$xxxs;
  }

  &__file-input {
    display: none;
  }

  &__upload-drop-zone {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: spacing.$xxs;
    padding: spacing.$xxs;

    border-radius: border.$medium-radius;
    border: border.$primary;
    border-style: dashed;
  }

  &--mobile {
    #{ $self }__upload-input {
      flex: 1;
    }
  }
}
</style>
